/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import type { GetTenantReleasesResponse, TenantReleaseListItem } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

/**
 * Custom hook that fetches ALL tenant releases without any filtering.
 * This is used for the Release Management page where we need to show
 * the published status of all releases, not just the ones that match
 * specific business rules.
 *
 * @returns Object containing:
 *   - data: Array of all tenant releases
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - refetch: Function to manually refetch data
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
const PAGE_SIZE = 100;

export function useAllTenantReleasesQuery() {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const query = useQuery({
    queryKey: ['all-tenant-releases', selectedTenantId],
    queryFn: async (): Promise<TenantReleaseListItem[]> => {
      if (!selectedTenantId) {
        return [];
      }

      // Fetch first page to get total pages count
      const firstResponse: GetTenantReleasesResponse = await mgmtPlaneAPIClient.tenantReleases.getTenantReleases({
        tenantId: selectedTenantId,
        page: 1,
        pageSize: PAGE_SIZE,
      });

      const { totalPages } = firstResponse.meta.page;

      if (totalPages <= 1) {
        return firstResponse.data;
      }

      // Fetch remaining pages in parallel
      const remainingPageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const remainingResponses = await Promise.all(
        remainingPageNumbers.map((page) =>
          mgmtPlaneAPIClient.tenantReleases.getTenantReleases({
            tenantId: selectedTenantId,
            page,
            pageSize: PAGE_SIZE,
          }),
        ),
      );

      return [firstResponse, ...remainingResponses].flatMap((response) => response.data);
    },
    enabled: !!selectedTenantId,
    staleTime: 10 * 1000, // 10 seconds
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
